import { RouteProp, useRoute } from "@react-navigation/native";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { createStore, Provider } from "jotai";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { AgendaLayout } from "@whoosh/design-system/src/apps/PatronApp/layout/AgendaLayout";
import { Loader } from "@whoosh/design-system/src/components/display";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { useFacilityLessonBookingDays } from "../../../../features/Facility/hooks/useFacilityLessonBookingDays";
import { LessonsStackNavigatorParamList } from "../../../../navigators/LessonsStackNavigator/types";
import { LessonsSignedOutBanner } from "../../components/LessonsSignedOutBanner";
import { InstructorDetailScreenQuery } from "./__generated__/InstructorDetailScreenQuery.graphql";
import { durationAtom, lessonTypeIdAtom } from "./atoms";
import { InstructorAvailabilityList } from "./components/InstructorAvailabilityList";
import { InstructorHeader } from "./components/InstructorHeader";

export const InstructorDetailScreen = () => {
  const route =
    useRoute<RouteProp<LessonsStackNavigatorParamList, "InstructorDetail">>();

  const facilitySlug = route?.params?.facilitySlug;
  const instructorId = route?.params?.instructorId;
  const duration = route?.params?.duration;
  const lessonTypeId = route?.params?.lessonTypeId;

  const [queryRef, loadQuery] = useQueryLoader<InstructorDetailScreenQuery>(
    InstructorDetailScreenQueryString
  );

  const fetchInstructorDetailQuery = useCallback(() => {
    if (loadQuery && facilitySlug && instructorId) {
      loadQuery(
        { facilitySlug, instructorId },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [facilitySlug, instructorId, loadQuery]);

  useEffect(() => {
    fetchInstructorDetailQuery();
  }, [fetchInstructorDetailQuery]);

  const store = useMemo(() => {
    const s = createStore();
    s.set(durationAtom, duration ? parseInt(duration, 10) : undefined);
    s.set(lessonTypeIdAtom, lessonTypeId ?? undefined);
    return s;
  }, [duration, lessonTypeId]);

  return (
    <Box
      flex={1}
      safeAreaTop
      bg="surface-primary"
      testID="instructor-detail-screen"
    >
      <WhiteBoxToCoverHeaderShadow />
      <Provider store={store}>
        <Suspense fallback={<Loader />}>
          {queryRef ? (
            <InstructorDetailScreenInner
              queryRef={queryRef}
              refetchCallback={fetchInstructorDetailQuery}
            />
          ) : null}
        </Suspense>
      </Provider>
    </Box>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<InstructorDetailScreenQuery>;
  refetchCallback: () => void;
};
const InstructorDetailScreenInner = ({
  queryRef,
  refetchCallback,
}: InnerProps) => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [completeCallback, setCompleteCallback] = useState(() => () => {
    refetchCallback();
    setIsAuthModalOpen(false);
  });
  const isMobile = useResponsiveProp({ base: 1, lg: 0 });
  const data = usePreloadedQuery(InstructorDetailScreenQueryString, queryRef);

  const bookingWindowDays = useFacilityLessonBookingDays(data.facility ?? null);

  const [scrolled, setScrolled] = useState(false);

  const openAuthModalWithCallback = (callback: () => void) => {
    setCompleteCallback(() => () => {
      callback();
      setIsAuthModalOpen(false);
    });
    setIsAuthModalOpen(true);
  };

  const cancelCallback = useCallback(() => {
    setCompleteCallback(() => () => {
      refetchCallback();
      setIsAuthModalOpen(false);
    });
    setIsAuthModalOpen(false);
  }, [refetchCallback]);

  return (
    <AgendaLayout
      shouldCenter
      offsetTop={isMobile ? true : false}
      header={
        <InstructorHeader
          instructorRef={data.node ?? null}
          sessionRef={data.session ?? null}
          scrolled={scrolled}
        />
      }
      agenda={
        <Box
          flex={1}
          borderLeftWidth={isMobile ? 0 : 1}
          borderColor="outline-default"
        >
          <InstructorAvailabilityList
            facilityRef={data.facility ?? null}
            setScrolled={setScrolled}
            openAuthModal={openAuthModalWithCallback}
            bookingWindowDays={bookingWindowDays}
          />
          <LessonsSignedOutBanner
            sessionRef={data.session ?? null}
            completeCallback={completeCallback}
            cancelCallback={cancelCallback}
            authModalState={[isAuthModalOpen, setIsAuthModalOpen]}
          />
        </Box>
      }
      sidebarContent={<></>}
    />
  );
};

const InstructorDetailScreenQueryString = graphql`
  query InstructorDetailScreenQuery(
    $facilitySlug: String!
    $instructorId: ID!
  ) {
    node(id: $instructorId) {
      ... on StaffProfile {
        ...InstructorHeaderFragment_staffProfile
        id
      }
    }
    session {
      ...InstructorHeaderFragment_session
      ...LessonsSignedOutBanner_session
    }
    facility(slug: $facilitySlug) {
      ...useFacilityLessonBookingDays_facility
      ...InstructorAvailabilityList_facility
    }
  }
`;

const WhiteBoxToCoverHeaderShadow = () => {
  return (
    <Box
      height={60}
      style={{ zIndex: 1, marginTop: -60 }}
      bg="surface-primary"
    />
  );
};
